// Shared vocabulary for the Refunds, disputes & review page.
// Oct 3 2026 (Randy) — the page used to render ten different money events at
// identical weight, each one leading with a kind code and twelve monospace
// identifiers. This file is where the plain language lives instead.

export const fmt$ = (cents) => {
  const n = (cents || 0) / 100;
  return `${n < 0 ? "−" : ""}$${Math.abs(n).toFixed(2)}`;
};

// Fired after any resolve / acknowledge / refund, so the Financials banner and
// the nav badge — which live outside this page — stop showing a count the
// operator has just cleared.
export const REVIEW_MUTATED = "mysilo:review-mutated";

// What the human calls it. No kind codes on screen, ever.
export const KIND_LABEL = {
  refund_exceeds_unused_credit: "Refund went past the unused credit",
  dispute_lost_consumed_value: "Lost dispute on credit already spent",
  dfy_refund_needs_review: "Done-for-You order reversed",
  creator_funds_dispute_lost: "Chargeback lost on creator earnings",
  affiliate_commission_unrecovered: "Commission already paid on a refunded sale",
  affiliate_reversal_needs_review: "Commission reversal couldn't be calculated",
  dispute_opened_credit_held: "Dispute open — credit frozen",
  creator_funds_dispute_open: "Dispute open on creator earnings",
  creator_pro_access_revoked: "Creator Pro reversed — access removed",
  partial_refund_needs_review: "Partial refund — access left as it was",
  creator_funds_refund_exposure: "Refund on creator earnings",
  deferred_webhook_never_matched: "Held webhook never matched a purchase",
};

export const BANDS = [
  {
    id: "decision",
    label: "Needs your decision",
    blurb: "Money the system deliberately did not absorb. Nothing moves until you say so.",
    head: "bg-[#FDF8EE]",
    icon: "warning",
  },
  {
    id: "clock",
    label: "Clock running — act in Stripe",
    blurb: "Nothing on this page can answer a bank. An unanswered dispute is lost by default.",
    head: "bg-[#FDF3F0]",
    icon: "clock",
  },
  {
    id: "record",
    label: "For the record",
    blurb: "Already handled end to end. Nothing to decide — one tap files it.",
    head: "bg-[#F4F6F4]",
    icon: "receipt",
  },
];

const BAND_RANK = { clock: 0, decision: 1, record: 2 };

export const deadlineInfo = (iso) => {
  if (!iso) return null;
  const due = new Date(iso);
  const ms = due.getTime() - Date.now();
  const day = due.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  if (ms <= 0) return { label: `Evidence deadline passed · ${day}`, tone: "over" };
  const hrs = ms / 36e5;
  if (hrs < 24) return { label: `${Math.max(1, Math.round(hrs))}h left to send evidence`, tone: "over" };
  const days = Math.floor(hrs / 24);
  return {
    label: `${days} day${days === 1 ? "" : "s"} left to send evidence · due ${day}`,
    tone: days <= 4 ? "soon" : "ok",
  };
};

export const DEADLINE_STYLE = {
  over: "border-[#C16E5A] text-[#7A3A2A] bg-[#FDF3F0]",
  soon: "border-[#D9A97A] text-[#8A5A00] bg-[#FDF8EE]",
  ok: "border-[#B7CBB9] text-[#2F6B3A] bg-[#F1F7F1]",
};

const flowWords = (f) =>
  ({ fan_subscription: "supporter subscription", tip: "tip", pay_forward: "pay-it-forward payment" }[f]
    || (f || "payment").replace(/_/g, " "));

// ONE sentence, in dollars, leading with what happened to the money. The long
// prose the server writes (policy paragraph and all) moves behind Technical
// details.
export const moneyStory = (item) => {
  const e = item.extra || {};
  const amt = fmt$(item.amount_cents);
  switch (item.kind) {
    case "creator_funds_refund_exposure":
      return `${fmt$(e.gross_cents)} on a ${flowWords(e.flow)} was refunded. ${fmt$(e.creator_share_cents)} had already gone to the creator, so ${amt} is the platform's exposure — nothing was taken back from anyone.`;
    case "creator_funds_dispute_open":
      return `A ${fmt$(e.gross_cents)} ${flowWords(e.flow)} is being disputed by the cardholder. ${fmt$(e.creator_share_cents)} is already with the creator and has not been touched.`;
    case "creator_funds_dispute_lost":
      return `A ${fmt$(e.gross_cents)} ${flowWords(e.flow)} was charged back and the dispute was lost. ${amt} sits with the platform; nothing was deducted from the creator.`;
    case "creator_pro_access_revoked": {
      const n = (e.revoked_skus || []).length;
      return `A ${fmt$(e.paid_cents)} Creator Pro purchase was reversed, so ${n ? `${n} tool${n === 1 ? "" : "s"} w${n === 1 ? "as" : "ere"} removed` : "nothing had to be removed — it is all owned another way"}${e.affiliate_revoked ? ", and affiliate status closed" : ""}. No debt was created.`;
    }
    case "partial_refund_needs_review":
      return `${amt} was refunded on a ${fmt$(e.paid_cents)} purchase. A partial refund never removes access on its own, so everything was left exactly as it was.`;
    case "refund_exceeds_unused_credit":
      return `${fmt$(e.cumulative_refunded_cents)} has been refunded on this top-up but only ${fmt$(e.unused_cents)} of credit was still unused. ${amt} had already been spent or withdrawn, so it cannot come back off a balance.`;
    case "dispute_lost_consumed_value":
      return `This dispute was lost and ${amt} of the credit it bought had already been spent on AI usage or taken as cash. ${fmt$(e.frozen_reversed_cents)} of frozen credit was reversed.`;
    case "dispute_opened_credit_held":
      return `${amt} of unused credit is frozen while the card issuer decides${e.dispute_reason ? ` (reason given: ${String(e.dispute_reason).replace(/_/g, " ")})` : ""}. It releases itself if the dispute is won.`;
    case "deferred_webhook_never_matched":
      return `A ${amt} payment event waited 24h for a purchase that never appeared. Nothing was frozen and nothing was reversed — worth a look when convenient.`;
    case "affiliate_commission_unrecovered":
      return `${amt} of commission had already been paid out when this sale was reversed. Nothing was debited from the affiliate and no debt was created.`;
    case "affiliate_reversal_needs_review":
      return `A reversed charge has no recorded payment amount, so no proportional commission reversal could be worked out. The ledger needs checking by hand.`;
    default:
      return item.detail || `${amt} needs attention.`;
  }
};

// What the system already did, said plainly — so "nothing to do" is visible
// without reading a paragraph.
export const alreadyDone = (kind) =>
  ({
    refund_exceeds_unused_credit: "Credit reversed as far as it could go. Nothing deducted beyond it.",
    dispute_lost_consumed_value: "Frozen credit reversed. Nothing taken from any balance.",
    dfy_refund_needs_review: "Order flagged reversed; an unpaid operator payout was reversed.",
    creator_funds_dispute_lost: "Recorded. Nothing taken from the creator.",
    affiliate_commission_unrecovered: "Recorded. No debit attempted.",
    affiliate_reversal_needs_review: "Nothing changed on the affiliate ledger.",
    dispute_opened_credit_held: "Unused credit frozen; the creator was told why.",
    creator_funds_dispute_open: "Recorded, the creator was emailed, access left running.",
    creator_pro_access_revoked: "Access removed, the buyer was emailed, no debt created.",
    partial_refund_needs_review: "Access deliberately untouched.",
    creator_funds_refund_exposure: "Exposure recorded, creator untouched, a held tip voided if there was one.",
    deferred_webhook_never_matched: "Payload kept. Nothing applied.",
  }[kind] || "Recorded.");

// One incident, one entry. A lost dispute on spent credit can raise three items
// (held → lost → exposure); grouping on charge or dispute id makes that read as
// one story with three stamps instead of three unrelated chores.
export const groupIncidents = (items) => {
  const byKey = new Map();
  (items || []).forEach((it) => {
    const key = it.dispute_id || it.stripe_charge_id || it.id;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(it);
  });
  return [...byKey.entries()].map(([key, group]) => {
    const sorted = [...group].sort(
      (a, b) => (BAND_RANK[a.band] ?? 1) - (BAND_RANK[b.band] ?? 1)
        || new Date(b.created_at) - new Date(a.created_at));
    const open = sorted.filter((i) => i.status === "open");
    const primary = open[0] || sorted[0];
    return {
      key,
      band: primary.band || "decision",
      primary,
      items: [...group].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)),
      openCount: open.length,
      charge_id: primary.stripe_charge_id,
      dispute_id: primary.dispute_id,
    };
  }).sort((a, b) => new Date(b.primary.created_at) - new Date(a.primary.created_at));
};
